import { Service, inject } from '@angular/core'
import type { HttpRequest } from '@angular/common/http'
import { TaskQuery } from '@awork/features/task/state/task.query'
import { BatchOperation, batchURLMap } from '@awork/features/task/services/task-service/types'
import replace from '@awork/_shared/functions/replace'
import type { UndoActionType } from './types'

type UndoField = 'status' | 'priority' | 'dates' | 'assignees' | 'changes'
const BATCH_FIELDS: Record<string, UndoField> = {
  [batchURLMap[BatchOperation.setStatus]]: 'status',
  [batchURLMap[BatchOperation.setPrio]]: 'priority',
  [batchURLMap[BatchOperation.setDueDate]]: 'dates',
  adjustdates: 'dates',
  [batchURLMap[BatchOperation.assignUsers]]: 'assignees',
  [batchURLMap[BatchOperation.unassignUsers]]: 'assignees',
  plannerbatch: 'changes'
}

/** Describes an undoable change using data already present in the task store. */
@Service()
export class UndoLabelService {
  private readonly taskQuery = inject(TaskQuery)

  /**
   * Names the affected field and task without loading more data.
   * @param {UndoActionType} type - The primary action type.
   * @param {HttpRequest<unknown>} request - The captured mutation request.
   * @param {string[]} taskIds - The distinct affected tasks.
   * @returns {string} A short label for the pending action.
   */
  build(type: UndoActionType, request: HttpRequest<unknown>, taskIds: string[]): string {
    const translations = q.translations.UndoService.label
    const field = this.field(type, request)
    const labels = {
      status: [translations.statusOne, translations.statusMany],
      priority: [translations.priorityOne, translations.priorityMany],
      dates: [translations.datesOne, translations.datesMany],
      assignees: [translations.assigneesOne, translations.assigneesMany],
      changes: [translations.changesOne, translations.changesMany]
    }

    if (taskIds.length > 1) {
      return replace(labels[field][1], { n: String(taskIds.length) })
    }

    let name = taskIds.length === 1 ? this.taskQuery.getEntity(taskIds[0])?.name : undefined
    const body = request.body

    if (
      !name &&
      request.method === 'PUT' &&
      body &&
      typeof body === 'object' &&
      'name' in body &&
      typeof body.name === 'string'
    ) {
      name = body.name
    }

    if (!name || type === 'generic') {
      return translations.generic
    }

    const task = name.length > 40 ? `${name.slice(0, 40)}…` : name

    return replace(labels[field][0], { task })
  }

  /**
   * Maps composite batch records to the field shown by their route.
   * @param {UndoActionType} type - The recorded action type.
   * @param {HttpRequest<unknown>} request - The captured request.
   * @returns {UndoField} The field described by the label.
   */
  private field(type: UndoActionType, request: HttpRequest<unknown>): UndoField {
    if (type === 'task.batch') {
      const operation = request.url.split('?')[0].split('/').at(-1)?.toLowerCase() ?? ''

      return BATCH_FIELDS[operation] ?? 'changes'
    }

    const fields: Partial<Record<UndoActionType, UndoField>> = {
      'task.status': 'status',
      'task.priority': 'priority',
      'task.dates': 'dates',
      'task.assignees': 'assignees'
    }

    return fields[type] ?? 'changes'
  }
}
